import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock3,
  CreditCard,
  Download,
  FileText,
  LineChart,
  ListChecks,
  Mail,
  PieChart,
  Plus,
  Rocket,
  Target,
  TrendingDown,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { createAdminLead, getAdminJourneyDashboard, getAdminLeads, getAdminProposals } from "../../lib/api";
import type { AdminProposalRecord, JourneySummary } from "../../lib/types";

const pipelineStages = [
  "New Inquiry",
  "Discovery Email Sent",
  "Discovery Scheduled",
  "Discovery Completed",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Proposal Viewed",
  "Proposal Accepted",
  "Agreement Sent",
  "Agreement Viewed",
  "Agreement Signed",
  "Payment Pending",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Welcome Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Kickoff Call Scheduled",
  "Kickoff Call Confirmed",
  "Kickoff Call Completed",
  "Project Active",
  "Build In Progress",
  "Internal Review",
  "Client Review",
  "Revision Requested",
  "Final Approval",
  "Project Complete",
  "Delivered",
  "Archived",
] as const;

const sourceLabels = [
  "Website",
  "Google Search",
  "Google Business Profile",
  "Referral",
  "Partner Referral",
  "Facebook",
  "Instagram",
  "LinkedIn",
  "YouTube",
  "Networking",
  "Existing Client",
  "Community Event",
  "Word of Mouth",
  "Direct Outreach",
  "Other",
] as const;

type AdminLeadRecord = Awaited<ReturnType<typeof getAdminLeads>>["leads"][number];

type ManualLeadForm = {
  fullName: string;
  email: string;
  phone: string;
  eventType: string;
  eventDate: string;
  source: string;
  serviceInterest: string;
  notes: string;
  estimatedValue: string;
};

type Analytics = ReturnType<typeof buildAnalytics>;

export function ExecutiveDashboardPage() {
  const [journeys, setJourneys] = useState<JourneySummary[]>([]);
  const [leads, setLeads] = useState<AdminLeadRecord[]>([]);
  const [proposals, setProposals] = useState<AdminProposalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualLead, setManualLead] = useState<ManualLeadForm>({
    fullName: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    source: "Manual Admin Entry",
    serviceInterest: "",
    notes: "",
    estimatedValue: "",
  });
  const [savingLead, setSavingLead] = useState(false);
  const [leadMessage, setLeadMessage] = useState("");

  useEffect(() => {
    document.title = "Executive Dashboard | Business Center OS";
    void loadDashboard();
  }, []);

  async function loadDashboard() {
    setLoading(true);
    setError("");
    try {
      const [journeyData, leadData, proposalData] = await Promise.all([getAdminJourneyDashboard(), getAdminLeads(), getAdminProposals()]);
      setJourneys(journeyData.journeys || []);
      setLeads(leadData.leads || []);
      setProposals(proposalData.proposals || []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load executive dashboard.");
    } finally {
      setLoading(false);
    }
  }

  const analytics = useMemo(() => buildAnalytics(journeys, leads), [journeys, leads]);
  const proposalStats = useMemo(() => buildProposalStats(proposals), [proposals]);

  async function submitManualLead() {
    setSavingLead(true);
    setLeadMessage("");
    try {
      await createAdminLead({
        fullName: manualLead.fullName,
        email: manualLead.email,
        phone: manualLead.phone,
        eventType: manualLead.eventType,
        eventDate: manualLead.eventDate,
        source: manualLead.source || "Manual Admin Entry",
        serviceInterest: manualLead.serviceInterest,
        notes: manualLead.notes,
        estimatedValue: manualLead.estimatedValue,
        isDraft: !manualLead.fullName || !manualLead.email,
        activityNote: "Lead Created Manually By Admin",
      });
      setManualLead({
        fullName: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        source: "Manual Admin Entry",
        serviceInterest: "",
        notes: "",
        estimatedValue: "",
      });
      setLeadMessage("Manual lead created successfully.");
      await loadDashboard();
    } catch (createError) {
      setLeadMessage(createError instanceof Error ? createError.message : "Unable to create manual lead.");
    } finally {
      setSavingLead(false);
    }
  }

  const exportPayload = useMemo(() => {
    const rows = journeys.map((journey) => ({
      leadName: journey.leadName,
      email: journey.leadEmail,
      source: journey.leadSource,
      status: journey.currentStatus,
      proposalStatus: journey.proposalStatus,
      agreementStatus: journey.agreementStatus,
      paymentStatus: journey.paymentStatus,
      projectStatus: journey.projectStatus,
      amountDue: money(journey.amountDueCents),
      amountPaid: money(journey.amountPaidCents),
      amountRemaining: money(journey.amountRemainingCents),
    }));
    return rows;
  }, [journeys]);

  function downloadCsv(kind: "sales" | "leads" | "report") {
    const csv =
      kind === "leads"
        ? toCsv(leads.map((lead) => ({
            leadId: lead.leadId,
            name: lead.fullName,
            email: lead.email,
            phone: lead.phone,
            source: lead.source,
            status: lead.status,
            eventType: lead.eventType,
            eventDate: lead.eventDate,
            serviceInterest: lead.serviceInterest,
            referralSource: lead.referralSource,
            marketingSource: lead.marketingSource,
          })))
        : kind === "report"
          ? toCsv(analytics.reports)
          : toCsv(exportPayload);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = kind === "leads" ? "ktd-leads-export.csv" : kind === "report" ? "ktd-executive-report.csv" : "ktd-sales-pipeline.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function printPdf() {
    window.print();
  }

  return (
    <BusinessCenterOSAdminShell
      title="Executive Dashboard"
      description="Monitor business performance, client activity, operational priorities, revenue movement, and workspace health across Business Center OS."
      statusItems={[
        { label: "Leads", value: journeys.length },
        { label: "Collected revenue", value: money(analytics.collectedRevenue) },
        { label: "Open pipeline", value: money(analytics.openPipelineValue) },
        { label: "Projects at risk", value: analytics.projectsAtRisk.length },
      ]}
    >
      <div className="command-center-shell grid gap-8 text-[#fff9ef]">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard icon={Users} label="Leads" value={journeys.length} />
            <KpiCard icon={CreditCard} label="Collected Revenue" value={money(analytics.collectedRevenue)} />
            <KpiCard icon={Target} label="Open Pipeline Value" value={money(analytics.openPipelineValue)} />
            <KpiCard icon={AlertTriangle} label="Projects At Risk" value={analytics.projectsAtRisk.length} />
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {analytics.journeyMetrics.map((metric) => (
              <MetricTile key={metric.label} label={metric.label} value={metric.value} note={metric.note} />
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading
                icon={BarChart3}
                title="Sales Pipeline"
                subtitle="Counts, dollar value, and conversion through each stage."
              />
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {analytics.pipeline.map((stage) => (
                  <StageCard key={stage.label} stage={stage} />
                ))}
              </div>
            </section>

            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading
                icon={PieChart}
                title="Lead Source Analytics"
                subtitle="Where opportunities originate and which channels convert best."
              />
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {analytics.sourceHighlights.map((highlight) => (
                  <InsightCard key={highlight.label} title={highlight.label} value={highlight.value} note={highlight.note} />
                ))}
              </div>
              <div className="mt-5 grid gap-4">
                {analytics.sources.slice(0, 8).map((source) => (
                  <SourceRow key={source.label} source={source} max={analytics.maxSourceCount} />
                ))}
              </div>
            </section>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.15fr_.85fr]">
            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={Activity} title="Discovery Analytics" subtitle="Discovery call health and conversion signals." />
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {analytics.discoveryMetrics.map((metric) => (
                  <MetricTile key={metric.label} label={metric.label} value={metric.value} note={metric.note} />
                ))}
              </div>
            </section>

            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={LineChart} title="Revenue Overview" subtitle="Collected revenue, pending revenue, balances, and payment progress." />
              <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {analytics.revenueOverview.map((metric) => (
                  <MetricTile key={metric.label} label={metric.label} value={metric.value} note={metric.note} />
                ))}
              </div>
            </section>
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              icon={FileText}
              title="Proposal Records"
              subtitle="Live proposal records pulled directly from the Business Center OS proposal tables."
            />
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {proposalStats.cards.map((card) => (
                <MetricTile key={card.label} label={card.label} value={card.value} note={card.note} />
              ))}
            </div>
            <div className="mt-5 grid gap-3 xl:grid-cols-2">
              {proposalStats.recent.length ? (
                proposalStats.recent.map((proposal) => (
                  <div key={proposal.id} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{proposal.proposalNumber}</p>
                        <p className="mt-1 font-bold text-[#fff9ef]">{proposal.leadName}</p>
                        <p className="text-sm text-[#fff9ef]/72">
                          {proposal.leadEmail} · {proposal.leadSource}
                        </p>
                      </div>
                      <span className="rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                        {proposal.leadStatus === "Proposal Approved to Send" ? "Ready to Send" : proposal.status}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-1 text-sm text-[#fff9ef]/72">
                      <p>{proposal.packageName || "Custom package"}</p>
                      <p>
                        {money(proposal.totalCents)} total · {proposal.lineItemCount} line item{proposal.lineItemCount === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link to={`/admin/leads/${proposal.leadId}`} className="admin-secondary-button" title="Open the linked lead record">
                        Open Lead
                      </Link>
                      <Link to="/admin/proposals" className="admin-primary-button" title="Open the proposal creation workspace">
                        Edit Proposal
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-white/15 bg-black/15 p-4 text-sm font-semibold text-[#fff9ef]/72">
                  No proposal records yet. Create your first draft in Create Proposal and it will appear here.
                </div>
              )}
            </div>
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              icon={Activity}
              title="Client Journey Timeline"
              subtitle="A live chronological feed from inquiry through delivery across the current journey records."
            />
            <div className="mt-5 grid gap-3">
              {analytics.journeyTimeline.slice(0, 12).map((item) => (
                <div key={`${item.leadId}-${item.createdAt}`} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-bold text-[#fff9ef]">{item.action}</p>
                    <span className="text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{formatDateTime(item.createdAt)}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#fff9ef]/72">
                    {item.leadName} {item.notes ? `· ${item.notes}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-[.95fr_1.05fr]">
            <section id="follow-up-center" className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={ListChecks} title="Follow-Up Center" subtitle="Proposal, agreement, payment, and client review follow-up queues." />
              <div className="mt-5 grid gap-3">
                {analytics.followUpBuckets.map((bucket) => (
                  <div key={bucket.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{bucket.label}</p>
                      <span className="rounded-full bg-[#FF6A2B] px-3 py-1 text-xs font-black text-[#050505]">{bucket.items.length}</span>
                    </div>
                    <div className="mt-3 grid gap-2">
                      {bucket.items.length ? (
                        bucket.items.slice(0, 3).map((item) => (
                          <div key={`${item.leadId}-${item.action}`} className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm">
                            <p className="font-bold text-[#fff9ef]">{item.leadName}</p>
                            <p className="text-[#fff9ef]/70">{item.action}</p>
                          </div>
                        ))
                      ) : (
                        <p className="rounded-lg border border-dashed border-white/12 bg-black/10 px-4 py-3 text-sm font-semibold text-[#fff9ef]/68">
                          No active follow-ups in this queue yet.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading
                icon={Clock3}
                title="Capacity Dashboard"
                subtitle="Where the operation is stretched and where attention is needed."
              />
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {analytics.capacityMetrics.map((metric) => (
                  <MetricTile key={metric.label} label={metric.label} value={metric.value} />
                ))}
              </div>
              <div className="mt-4 grid gap-3">
                {analytics.projectsAtRisk.slice(0, 5).map((item) => (
                  <div key={item.leadId} className="rounded-lg border border-red-400/20 bg-red-500/10 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-bold text-[#fff9ef]">{item.leadName}</p>
                      <span className="rounded-full border border-red-400/30 px-3 py-1 text-xs font-black text-red-100">{item.daysInStage}d</span>
                    </div>
                    <p className="mt-2 text-sm text-[#fff9ef]/72">{item.currentStatus}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_.9fr]">
            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={Mail} title="Referral & Partner Dashboard" subtitle="Lead sources, conversion, and estimated referral payouts." />
              <div className="mt-5 grid gap-3">
                {analytics.referralLeaderboard.slice(0, 8).map((item) => (
                  <div key={item.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{item.label}</p>
                      <p className="text-sm font-bold text-[#fff9ef]">{item.revenue}</p>
                    </div>
                    <p className="mt-2 text-sm text-[#fff9ef]/72">
                      {item.conversions} converted clients · {item.count} referrals · payout {item.payout}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={TrendingDown} title="Executive Reports" subtitle="Export lead, pipeline, source, and revenue reporting." />
              <div className="mt-5 grid gap-3">
                <button type="button" onClick={() => downloadCsv("sales")} className="admin-export-button" title="Download the current sales pipeline as CSV">
                  <Download className="size-4" aria-hidden="true" />
                  Download Sales Pipeline CSV
                </button>
                <button type="button" onClick={() => downloadCsv("leads")} className="admin-export-button" title="Download the current leads list as CSV">
                  <Download className="size-4" aria-hidden="true" />
                  Download Leads CSV
                </button>
                <button type="button" onClick={() => downloadCsv("report")} className="admin-export-button" title="Download the executive report as CSV">
                  <Download className="size-4" aria-hidden="true" />
                  Download Executive Report CSV
                </button>
                <button type="button" onClick={printPdf} className="admin-export-button" title="Print the executive report for PDF saving">
                  <Download className="size-4" aria-hidden="true" />
                  Print / Save as PDF
                </button>
              </div>
            </section>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.05fr_.95fr]">
            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={Activity} title="Activity Feed" subtitle="Newest business activity first." />
              <div className="mt-5 grid gap-3">
                {analytics.activityFeed.slice(0, 12).map((item) => (
                  <div key={`${item.leadId}-${item.createdAt}`} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-bold text-[#fff9ef]">{item.action}</p>
                      <span className="text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{formatDateTime(item.createdAt)}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#fff9ef]/72">
                      {item.leadName} {item.notes ? `· ${item.notes}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading icon={Rocket} title="Manual Lead Wizard" subtitle="Create a lead without the public inquiry form." />
              <div className="mt-5 grid gap-3">
                <WizardField label="Name" value={manualLead.fullName} onChange={(value) => setManualLead((current) => ({ ...current, fullName: value }))} />
                <WizardField label="Email" value={manualLead.email} onChange={(value) => setManualLead((current) => ({ ...current, email: value }))} />
                <WizardField label="Phone" value={manualLead.phone} onChange={(value) => setManualLead((current) => ({ ...current, phone: value }))} />
                <WizardField label="Event Type" value={manualLead.eventType} onChange={(value) => setManualLead((current) => ({ ...current, eventType: value }))} />
                <WizardField label="Event Date" type="date" value={manualLead.eventDate} onChange={(value) => setManualLead((current) => ({ ...current, eventDate: value }))} />
                <WizardField label="Lead Source" value={manualLead.source} onChange={(value) => setManualLead((current) => ({ ...current, source: value }))} />
                <WizardField label="Service Interest" value={manualLead.serviceInterest} onChange={(value) => setManualLead((current) => ({ ...current, serviceInterest: value }))} />
                <WizardField label="Estimated Value" value={manualLead.estimatedValue} onChange={(value) => setManualLead((current) => ({ ...current, estimatedValue: value }))} />
                <WizardTextArea label="Notes" value={manualLead.notes} onChange={(value) => setManualLead((current) => ({ ...current, notes: value }))} />
                {leadMessage ? <p className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 px-4 py-3 text-sm font-bold text-[#fff9ef]">{leadMessage}</p> : null}
                <button type="button" onClick={() => void submitManualLead()} disabled={savingLead} className="admin-primary-button" title="Create a lead without using the public inquiry form">
                  <Plus className="size-4" aria-hidden="true" />
                  {savingLead ? "Creating..." : "Create Manual Lead"}
                </button>
              </div>
            </section>
          </div>

          {error ? <p className="rounded-lg border border-red-400/30 bg-red-500/10 px-5 py-4 text-sm font-bold text-red-100">{error}</p> : null}
          {loading ? <p className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 px-5 py-4 text-sm font-bold text-[#fff9ef]">Loading command center data...</p> : null}
      </div>
    </BusinessCenterOSAdminShell>
  );
}

function buildAnalytics(journeys: JourneySummary[], leads: AdminLeadRecord[]) {
  const pipeline = pipelineStages.map((label) => {
    const count = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === label).length;
    const amount = journeys
      .filter((journey) => canonicalStatus(journey.currentStatus) === label)
      .reduce((sum, journey) => sum + journey.amountDueCents, 0);
    const prior = pipelineStages.indexOf(label) === 0 ? journeys.length : journeys.filter((journey) => canonicalStatus(journey.currentStatus) === pipelineStages[pipelineStages.indexOf(label) - 1]).length;
    return {
      label,
      count,
      amount,
      conversion: prior ? count / prior : 0,
    };
  });

  const sourceMap = new Map<string, { count: number; converted: number; revenue: number }>();
  for (const journey of journeys) {
    const key = journey.leadSource || "Other";
    const bucket = sourceMap.get(key) || { count: 0, converted: 0, revenue: 0 };
    bucket.count += 1;
    if (canonicalStatus(journey.currentStatus) === "Paid In Full") bucket.converted += 1;
    bucket.revenue += journey.amountPaidCents;
    sourceMap.set(key, bucket);
  }
  const sources = [...sourceMap.entries()]
    .map(([label, bucket]) => ({
      label,
      count: bucket.count,
      converted: bucket.converted,
      revenue: bucket.revenue,
      conversionRate: bucket.count ? bucket.converted / bucket.count : 0,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const maxSourceCount = Math.max(1, ...sources.map((source) => source.count));

  const paymentPaidJourneys = journeys.filter((journey) => journey.paymentPaidAt);
  const revenueThisMonth = sumByDate(paymentPaidJourneys, "paymentPaidAt", 1);
  const revenueThisQuarter = sumByDate(paymentPaidJourneys, "paymentPaidAt", 3);
  const revenueThisYear = sumByDate(paymentPaidJourneys, "paymentPaidAt", 12);
  const collectedRevenue = journeys.reduce((sum, journey) => sum + journey.amountPaidCents, 0);
  const outstandingRevenue = journeys.reduce((sum, journey) => sum + journey.amountRemainingCents, 0);
  const openPipelineValue = journeys.filter((journey) => !["Paid In Full", "Project Complete", "Delivered", "Archived"].includes(canonicalStatus(journey.currentStatus))).reduce((sum, journey) => sum + journey.amountDueCents, 0);
  const pendingRevenue = journeys
    .filter((journey) => !["Paid In Full", "Project Complete", "Delivered", "Archived"].includes(canonicalStatus(journey.currentStatus)))
    .reduce((sum, journey) => sum + Math.max(journey.amountRemainingCents, journey.amountDueCents - journey.amountPaidCents), 0);
  const partialPaymentClients = journeys.filter((journey) => journey.paymentStatus === "Partial Payment Received");
  const paidInFullClients = journeys.filter((journey) => journey.paymentStatus === "Paid In Full");
  const discoveryScheduledCount = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Discovery Scheduled").length;
  const proposalsSentCount = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Proposal Sent").length;
  const agreementsSignedCount = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Agreement Signed").length;
  const paymentsReceivedCount = journeys.filter((journey) => ["Partial Payment Received", "Paid In Full"].includes(canonicalStatus(journey.currentStatus))).length;
  const welcomeSentCount = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Welcome Sent").length;
  const activeProjectsCount = journeys.filter((journey) => ["Project Active", "Build In Progress", "Internal Review", "Awaiting Client Review", "Client Review", "Revision Requested", "Final Approval"].includes(canonicalStatus(journey.currentStatus))).length;
  const completedProjectsCount = journeys.filter((journey) => ["Project Complete", "Delivered", "Archived"].includes(canonicalStatus(journey.currentStatus))).length;

  const discoveryMetrics = buildDiscoveryMetrics(journeys);
  const capacityMetrics = buildCapacityMetrics(journeys);
  const projectsAtRisk = journeys
    .map((journey) => {
      const daysInStage = Math.floor((Date.now() - new Date(journey.leadCreatedAt).getTime()) / 86400000);
      return { ...journey, daysInStage };
    })
    .filter((journey) => journey.daysInStage >= 10 && !["Paid In Full", "Delivered", "Archived"].includes(canonicalStatus(journey.currentStatus)))
    .sort((a, b) => b.daysInStage - a.daysInStage);

  const proposalStatuses = new Set(["Proposal Approved to Send", "Proposal Sent", "Proposal Viewed", "Proposal Revision Requested"]);
  const agreementStatuses = new Set(["Agreement Sent", "Agreement Viewed", "Agreement Signed"]);
  const paymentStatuses = new Set(["Payment Pending", "Invoice Created", "Invoice Sent", "Partial Payment Received", "Awaiting Final Payment"]);
  const clientReviewStatuses = new Set(["Project Active", "Build In Progress", "Internal Review", "Awaiting Client Review", "Client Review", "Revision Requested"]);

  const followUpBuckets = [
    {
      label: "Proposal Follow-Ups",
      items: journeys
        .filter((journey) => proposalStatuses.has(canonicalStatus(journey.currentStatus)))
        .slice(0, 6)
        .map((item) => ({
          leadId: item.leadId,
          leadName: item.leadName,
          action: item.nextStep || item.currentStatus,
        })),
    },
    {
      label: "Agreement Follow-Ups",
      items: journeys
        .filter((journey) => agreementStatuses.has(canonicalStatus(journey.currentStatus)))
        .slice(0, 6)
        .map((item) => ({
          leadId: item.leadId,
          leadName: item.leadName,
          action: item.nextStep || item.currentStatus,
        })),
    },
    {
      label: "Payment Follow-Ups",
      items: journeys
        .filter((journey) => paymentStatuses.has(canonicalStatus(journey.currentStatus)))
        .slice(0, 6)
        .map((item) => ({
          leadId: item.leadId,
          leadName: item.leadName,
          action: item.nextStep || item.currentStatus,
        })),
    },
    {
      label: "Client Review Follow-Ups",
      items: journeys
        .filter((journey) => clientReviewStatuses.has(canonicalStatus(journey.currentStatus)))
        .slice(0, 6)
        .map((item) => ({
          leadId: item.leadId,
          leadName: item.leadName,
          action: item.nextStep || item.currentStatus,
        })),
    },
    {
      label: "Welcome & Onboarding",
      items: journeys
        .filter((journey) => ["Welcome Sent", "Onboarding Sent", "Onboarding Complete", "Kickoff Call Scheduled", "Kickoff Call Confirmed", "Kickoff Call Completed"].includes(canonicalStatus(journey.currentStatus)))
        .slice(0, 6)
        .map((item) => ({
          leadId: item.leadId,
          leadName: item.leadName,
          action: item.nextStep || item.currentStatus,
        })),
    },
    {
      label: "Overdue",
      items: projectsAtRisk.slice(0, 6).map((item) => ({
        leadId: item.leadId,
        leadName: item.leadName,
        action: `${item.currentStatus} for ${item.daysInStage} days`,
      })),
    },
  ];

  const activityFeed = [...journeys]
    .flatMap((journey) =>
      journey.statusHistory.map((item) => ({
        leadId: journey.leadId,
        leadName: journey.leadName,
        action: item.action || item.status,
        notes: item.fromStatus ? `${item.fromStatus} -> ${item.status}` : item.status,
        createdAt: item.createdAt,
      })),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  type JourneyTimelineItem = {
    leadId: string;
    leadName: string;
    action: string;
    notes: string;
    createdAt: string;
  };

  const journeyTimeline = [...journeys]
    .flatMap((journey): JourneyTimelineItem[] => {
      const events: Array<JourneyTimelineItem | null> = [
        {
          leadId: journey.leadId,
          leadName: journey.leadName,
          action: "Inquiry Submitted",
          notes: `${journey.leadSource || "Website"} inquiry captured.`,
          createdAt: journey.leadCreatedAt,
        },
        journey.proposalCreatedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Proposal Created",
              notes: journey.proposalStatus === "Proposal Approved to Send" ? "Proposal prepared and ready to send." : "Proposal created in the workspace.",
              createdAt: journey.proposalCreatedAt,
            }
          : null,
        journey.proposalSentAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Proposal Sent",
              notes: "Proposal sent to the client.",
              createdAt: journey.proposalSentAt,
            }
          : null,
        journey.proposalViewedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Proposal Viewed",
              notes: "Client viewed the proposal.",
              createdAt: journey.proposalViewedAt,
            }
          : null,
        journey.proposalAcceptedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Proposal Accepted",
              notes: "Client accepted the proposal.",
              createdAt: journey.proposalAcceptedAt,
            }
          : null,
        journey.agreementCreatedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Agreement Sent",
              notes: "Agreement generated and sent.",
              createdAt: journey.agreementCreatedAt,
            }
          : null,
        journey.agreementViewedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Agreement Viewed",
              notes: "Client viewed the agreement.",
              createdAt: journey.agreementViewedAt,
            }
          : null,
        journey.agreementSignedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Agreement Signed",
              notes: "Client signed the agreement.",
              createdAt: journey.agreementSignedAt,
            }
          : null,
        journey.paymentCreatedAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Invoice Created",
              notes: "Invoice record created.",
              createdAt: journey.paymentCreatedAt,
            }
          : null,
        journey.paymentPaidAt
          ? {
              leadId: journey.leadId,
              leadName: journey.leadName,
              action: "Payment Received",
              notes: journey.amountRemainingCents > 0 ? "Partial payment received." : "Paid in full.",
              createdAt: journey.paymentPaidAt,
            }
          : null,
        ...journey.statusHistory.map((item) => ({
          leadId: journey.leadId,
          leadName: journey.leadName,
          action: item.action || item.status,
          notes: item.fromStatus ? `${item.fromStatus} -> ${item.status}` : item.status,
          createdAt: item.createdAt,
        })),
      ];
      return events.filter((item): item is JourneyTimelineItem => Boolean(item));
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const referralCounts = new Map<string, { count: number; converted: number; revenue: number }>();
  for (const lead of leads) {
    const referralKey = lead.referralSource || lead.marketingSource || lead.source || "Unknown";
    const bucket = referralCounts.get(referralKey) || { count: 0, converted: 0, revenue: 0 };
    bucket.count += 1;
    const journey = journeys.find((item) => item.leadId === lead.id);
    if (journey?.paymentStatus === "Paid In Full") bucket.converted += 1;
    bucket.revenue += journey?.amountPaidCents || 0;
    referralCounts.set(referralKey, bucket);
  }
  const referralLeaderboard = [...referralCounts.entries()]
    .map(([label, bucket]) => ({
      label,
      count: bucket.count,
      conversions: bucket.converted,
      revenue: money(bucket.revenue),
      payout: money(Math.round(bucket.revenue * 0.15)),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const topCountSource = sources[0];
  const topConversionSource = [...sources].sort((a, b) => b.conversionRate - a.conversionRate || b.count - a.count || a.label.localeCompare(b.label))[0];
  const topRevenueSource = [...sources].sort((a, b) => b.revenue - a.revenue || b.count - a.count || a.label.localeCompare(b.label))[0];

  const sourceHighlights = [
    {
      label: "Top Source",
      value: topCountSource?.label || "Awaiting data",
      note: topCountSource ? `${topCountSource.count} leads · ${money(topCountSource.revenue)} collected` : "No lead source data available yet.",
    },
    {
      label: "Top Conversion",
      value: topConversionSource ? `${Math.round(topConversionSource.conversionRate * 100)}%` : "Awaiting data",
      note: topConversionSource ? `${topConversionSource.converted} paid clients from ${topConversionSource.label}` : "No conversion history available yet.",
    },
    {
      label: "Top Revenue",
      value: topRevenueSource ? money(topRevenueSource.revenue) : money(0),
      note: topRevenueSource ? `Revenue attributed to ${topRevenueSource.label}` : "No revenue has been attributed to a source yet.",
    },
  ];

  const revenueOverview = [
    { label: "Collected Revenue", value: money(collectedRevenue), note: `${paidInFullClients.length} paid in full client${paidInFullClients.length === 1 ? "" : "s"}` },
    { label: "Pending Revenue", value: money(pendingRevenue), note: `${journeys.filter((journey) => journey.amountRemainingCents > 0).length} balances still open` },
    { label: "Pipeline Value", value: money(openPipelineValue), note: "Open proposals, agreements, and payment-ready accounts" },
    { label: "Outstanding Balances", value: money(outstandingRevenue), note: "Unpaid balances currently in the pipeline" },
    { label: "Partial Payments", value: partialPaymentClients.length, note: "Clients who have deposited but still owe a balance" },
    { label: "Paid In Full Clients", value: paidInFullClients.length, note: "Closed journey records with zero balance" },
  ];

  return {
    pipeline,
    sources,
    maxSourceCount,
    sourceHighlights,
    discoveryMetrics,
    journeyMetrics: [
      { label: "Discovery Calls Scheduled", value: discoveryScheduledCount, note: "Discovery calls ready on the calendar." },
      { label: "Proposals Sent", value: proposalsSentCount, note: "Opportunities that have moved into proposal mode." },
      { label: "Agreements Signed", value: agreementsSignedCount, note: "Signed agreements ready for payment creation." },
      { label: "Payments Received", value: paymentsReceivedCount, note: "Deposits or full payments captured so far." },
      { label: "Welcome Emails Sent", value: welcomeSentCount, note: "Clients welcomed into onboarding and kickoff." },
      { label: "Active Projects", value: activeProjectsCount, note: "Projects in production or client review." },
      { label: "Completed Projects", value: completedProjectsCount, note: "Projects delivered or archived." },
    ],
    revenueThisMonth,
    revenueThisQuarter,
    revenueThisYear,
    collectedRevenue,
    outstandingRevenue,
    openPipelineValue,
    pendingRevenue,
    revenueOverview,
    capacityMetrics,
    projectsAtRisk,
    followUpBuckets,
    journeyTimeline,
    activityFeed,
    referralLeaderboard,
    reports: [
      { metric: "Leads by Source", value: journeys.length },
      { metric: "Revenue by Source", value: money(collectedRevenue) },
      { metric: "Conversion by Source", value: `${Math.round((journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Paid In Full").length / Math.max(1, journeys.length)) * 100)}%` },
      { metric: "Sales Pipeline", value: money(openPipelineValue) },
      { metric: "Referral Performance", value: referralLeaderboard.length },
      { metric: "Outstanding Balances", value: money(outstandingRevenue) },
      { metric: "Project Status", value: capacityMetrics.find((item) => item.label === "Active Projects")?.value || 0 },
    ],
  };
}

function buildProposalStats(proposals: AdminProposalRecord[]) {
  const readyCount = proposals.filter((proposal) => proposal.leadStatus === "Proposal Approved to Send").length;
  const draftCount = proposals.filter((proposal) => proposal.status === "draft" && proposal.leadStatus !== "Proposal Approved to Send").length;
  const sentCount = proposals.filter((proposal) => proposal.status === "sent").length;
  const acceptedCount = proposals.filter((proposal) => proposal.status === "accepted").length;
  const totalValue = proposals.reduce((sum, proposal) => sum + proposal.totalCents, 0);
  const openValue = proposals
    .filter((proposal) => ["draft", "sent", "viewed", "revision_requested"].includes(proposal.status) || proposal.leadStatus === "Proposal Approved to Send")
    .reduce((sum, proposal) => sum + proposal.totalCents, 0);

  return {
    cards: [
      { label: "Draft Proposals", value: draftCount, note: "Internal drafts that are still being prepared." },
      { label: "Ready to Send", value: readyCount, note: "Proposals marked for admin review before publishing." },
      { label: "Sent Proposals", value: sentCount, note: "Proposals already sent to clients." },
      { label: "Accepted Proposals", value: acceptedCount, note: "Proposals that moved into agreement and payment steps." },
      { label: "Proposal Value", value: money(totalValue), note: "Total value across all proposal records." },
      { label: "Open Proposal Value", value: money(openValue), note: "Value still moving through the proposal pipeline." },
    ],
    recent: proposals.slice(0, 8),
  };
}

function buildDiscoveryMetrics(journeys: JourneySummary[]) {
  const scheduled = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Discovery Scheduled");
  const completed = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Discovery Completed");
  const noShows = journeys.filter((journey) => !["Discovery Completed", "Proposal Sent", "Proposal Accepted", "Agreement Sent", "Paid In Full"].includes(canonicalStatus(journey.currentStatus)));
  const rescheduled = journeys.filter((journey) => journey.statusHistory.some((item) => item.action.toLowerCase().includes("resched")));
  const conversionToProposal = journeys.filter((journey) => ["Proposal Sent", "Proposal Viewed", "Proposal Accepted", "Agreement Sent", "Paid In Full"].includes(canonicalStatus(journey.currentStatus))).length;
  const conversionToClient = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Paid In Full").length;
  return [
    { label: "Scheduled Calls", value: scheduled.length, note: "Discovery calls currently booked and ready." },
    { label: "Completed Calls", value: completed.length, note: "Discovery calls that were actually held." },
    { label: "No Shows", value: noShows.length, note: "Leads still waiting for a completed call." },
    { label: "Rescheduled Calls", value: rescheduled.length, note: "Calls that moved dates or were postponed." },
    { label: "Conversion to Proposal", value: `${Math.round((conversionToProposal / Math.max(1, scheduled.length)) * 100)}%`, note: "Booked calls that progressed into proposal mode." },
    { label: "Conversion to Client", value: `${Math.round((conversionToClient / Math.max(1, completed.length)) * 100)}%`, note: "Completed discovery calls that became paid clients." },
  ];
}

function buildCapacityMetrics(journeys: JourneySummary[]) {
  const activeClients = journeys.filter((journey) => !["Archived", "Delivered"].includes(canonicalStatus(journey.currentStatus))).length;
  const activeProjects = journeys.filter((journey) => ["Project Active", "Build In Progress", "Internal Review", "Client Review", "Revision Requested", "Final Approval"].includes(canonicalStatus(journey.currentStatus))).length;
  const awaitingContent = journeys.filter((journey) => canonicalStatus(journey.currentStatus) === "Awaiting Client Content").length;
  const inReview = journeys.filter((journey) => ["Internal Review", "Client Review", "Awaiting Client Review"].includes(canonicalStatus(journey.currentStatus))).length;
  const upcomingEvents = journeys.filter((journey) => !!journey.eventDate && !["Archived"].includes(canonicalStatus(journey.currentStatus))).length;
  const upcomingDeliveries = journeys.filter((journey) => ["Final Approval", "Project Complete", "Delivered"].includes(canonicalStatus(journey.currentStatus))).length;
  const projectsNearDeadline = journeys.filter((journey) => ["Build In Progress", "Internal Review", "Revision Requested"].includes(canonicalStatus(journey.currentStatus))).length;
  const overdueProjects = journeys.filter((journey) => ["Awaiting Client Content", "On Hold"].includes(canonicalStatus(journey.currentStatus))).length;
  return [
    { label: "Active Clients", value: activeClients },
    { label: "Active Projects", value: activeProjects },
    { label: "Awaiting Client Content", value: awaitingContent },
    { label: "Projects In Review", value: inReview },
    { label: "Upcoming Event Dates", value: upcomingEvents },
    { label: "Upcoming Deliveries", value: upcomingDeliveries },
    { label: "Projects Near Deadline", value: projectsNearDeadline },
    { label: "Overdue Projects", value: overdueProjects },
  ];
}

function sumByDate(journeys: JourneySummary[], key: keyof Pick<JourneySummary, "paymentPaidAt">, monthsBack: number) {
  const now = new Date();
  const since = new Date(now);
  since.setMonth(since.getMonth() - (monthsBack - 1));
  return journeys.reduce((sum, journey) => {
    const value = journey[key];
    if (!value) return sum;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()) || parsed < since) return sum;
    return sum + journey.amountPaidCents;
  }, 0);
}

function canonicalStatus(status: string) {
  if (status === "Inquiry Submitted") return "New Inquiry";
  return status;
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
}

function toCsv(rows: Array<Record<string, string | number | undefined | null>>) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  return [headers.join(","), ...rows.map((row) => headers.map((header) => escape(row[header])).join(","))].join("\n");
}

function KpiCard({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: string | number }) {
  const description =
    label === "Leads"
      ? "All prospects currently tracked in the Business Center OS journey."
      : label === "Collected Revenue"
        ? "Money already collected through paid invoices and completed payments."
        : label === "Open Pipeline Value"
          ? "Revenue still moving through proposal, agreement, or payment stages."
          : "Projects sitting too long in a stage and needing admin attention.";
  return (
    <article className="command-center-card rounded-lg p-4" title={description}>
      <Icon className="size-5 text-[#FF6A2B]" aria-hidden="true" />
      <p className="mt-4 font-display text-3xl font-bold text-[#fff9ef]">{value}</p>
      <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#fff9ef]/66">{label}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/72">{description}</p>
    </article>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }: { icon: typeof Users; title: string; subtitle: string }) {
  return (
    <div className="border-b border-[#FF6A2B]/14 pb-4">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">
        <Icon className="size-4" aria-hidden="true" />
        Executive Dashboard
      </p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">{title}</h2>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">{subtitle}</p>
    </div>
  );
}

function MetricTile({ label, value, note }: { label: string; value: string | number; note?: string }) {
  const description =
    label.includes("Revenue")
      ? "Closed revenue from completed payments within the selected time range."
      : label.includes("Outstanding")
        ? "Unpaid balances still waiting on client action."
        : label.includes("Conversion")
          ? "Performance derived from the current filtered journey set."
          : label.includes("Clients")
            ? "Client capacity and delivery workload visible at a glance."
            : "Supporting KPI pulled from the current filtered journey set.";
  return (
    <div className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={description}>
      <p className="text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{value}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/68">{note || description}</p>
    </div>
  );
}

function StageCard({ stage }: { stage: { label: string; count: number; amount: number; conversion: number } }) {
  return (
    <div className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={`${stage.label} stage summary`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{stage.label}</p>
        <span className="rounded-full bg-[#FF6A2B] px-3 py-1 text-xs font-black text-[#050505]">{stage.count}</span>
      </div>
      <p className="mt-2 text-sm font-semibold text-[#fff9ef]/72">{money(stage.amount)}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/70">
        How much revenue sits in this stage and how efficiently it moves forward.
      </p>
      <div className="mt-3 h-2 rounded-full bg-white/10">
        <div className="h-2 rounded-full bg-[#FF6A2B]" style={{ width: `${Math.max(8, Math.round(stage.conversion * 100))}%` }} />
      </div>
      <p className="mt-2 text-xs font-semibold text-[#fff9ef]/66">{Math.round(stage.conversion * 100)}% conversion</p>
    </div>
  );
}

function SourceRow({ source, max }: { source: { label: string; count: number; converted: number; revenue: number; conversionRate: number }; max: number }) {
  return (
    <div className="grid gap-2 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={`${source.label} lead source performance`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-[#fff9ef]">{source.label}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
          <span>{Math.round(source.conversionRate * 100)}%</span>
          <span className="rounded-full border border-[#FF6A2B]/24 px-2 py-1 text-[#fff9ef]/75">{money(source.revenue)}</span>
        </div>
      </div>
      <p className="text-sm font-semibold leading-6 text-[#fff9ef]/70">
        {source.count} leads, {source.converted} paid clients, and {money(source.revenue)} in collected revenue.
      </p>
      <div className="h-3 rounded-full bg-white/10">
        <div className="h-3 rounded-full bg-[#FF6A2B]" style={{ width: `${Math.max(8, Math.round((source.count / max) * 100))}%` }} />
      </div>
      <p className="text-xs font-semibold text-[#fff9ef]/66">Conversion rate and collected revenue for this source.</p>
    </div>
  );
}

function InsightCard({ title, value, note }: { title: string; value: string; note: string }) {
  return (
    <article className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4" title={note}>
      <p className="text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{title}</p>
      <p className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{value}</p>
      <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/70">{note}</p>
    </article>
  );
}

function WizardField({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="command-center-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
      />
    </label>
  );
}

function WizardTextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      {label}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} className="command-center-field focus-ring min-h-28 rounded-lg px-4 py-3 text-sm font-semibold" />
    </label>
  );
}
